// src/components/site-editor/panels/EventsPanel.js
// Site Editor panel for controlling the Events section visibility and display settings.
// Events themselves are managed from the dashboard (Events page).

import React, { useState, useEffect } from 'react';
import { ExternalLink, List, CalendarDays } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { useNavigate } from 'react-router-dom';
import Toggle from '../../Toggle';
import { auth, db } from '../../../config/firebase-config';
import { collection, query, where, getDocs } from 'firebase/firestore';

const EventsPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const navigate = useNavigate();

  const events = state.draft.content.events || {
    isVisible: false,
    sectionTitle: 'Upcoming Events',
    defaultView: 'list',
  };

  const [publishedCount, setPublishedCount] = useState(null);

  // Count published events
  useEffect(() => {
    const uid = auth?.currentUser?.uid;
    if (!uid || !db) return;
    (async () => {
      try {
        const q = query(
          collection(db, 'practitioners', uid, 'events'),
          where('status', '==', 'published'),
        );
        const snap = await getDocs(q);
        setPublishedCount(snap.size);
      } catch (err) {
        console.warn('Failed to count published events:', err.message);
        setPublishedCount(0);
      }
    })();
  }, []);

  const handleUpdate = (field, value) => {
    actions.updateContent('events', {
      ...events,
      [field]: value,
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Events</h2>
        <p className="text-sm text-gray-600">
          Display your upcoming events on your website with a card list or calendar view.
        </p>
      </div>

      {/* Visibility toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', background: '#F9FAFB', borderRadius: 8,
        border: '1px solid #E5E7EB',
      }}>
        <div>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#111827', fontFamily: 'Montserrat, sans-serif', margin: 0 }}>
            Show events section on your website
          </p>
        </div>
        <Toggle
          enabled={events.isVisible}
          onChange={(val) => handleUpdate('isVisible', val)}
        />
      </div>

      {/* Settings — only show when visible */}
      {events.isVisible && (
        <>
          {/* Section title */}
          <div>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 600, color: '#374151',
              marginBottom: 6, fontFamily: 'Montserrat, sans-serif',
            }}>
              Section title
            </label>
            <input
              type="text"
              value={events.sectionTitle || ''}
              onChange={(e) => handleUpdate('sectionTitle', e.target.value)}
              placeholder="Upcoming Events"
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB',
                borderRadius: 8, fontSize: 14, fontFamily: 'Montserrat, sans-serif',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Default view */}
          <div>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 600, color: '#374151',
              marginBottom: 8, fontFamily: 'Montserrat, sans-serif',
            }}>
              Default display
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { value: 'list', label: 'Card list view', icon: List },
                { value: 'calendar', label: 'Calendar view', icon: CalendarDays },
              ].map(opt => {
                const isSelected = events.defaultView === opt.value;
                const Icon = opt.icon;
                return (
                  <label
                    key={opt.value}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      border: isSelected ? '2px solid #7c3aed' : '1px solid #D1D5DB',
                      background: isSelected ? '#F5F3FF' : '#FFFFFF',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="defaultView"
                      value={opt.value}
                      checked={isSelected}
                      onChange={() => handleUpdate('defaultView', opt.value)}
                      style={{ display: 'none' }}
                    />
                    <Icon size={16} color={isSelected ? '#7c3aed' : '#9CA3AF'} />
                    <span style={{
                      fontSize: 13, fontWeight: 500,
                      fontFamily: 'Montserrat, sans-serif',
                      color: isSelected ? '#7c3aed' : '#374151',
                    }}>
                      {opt.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Published events count */}
          {publishedCount !== null && (
            <div style={{
              padding: '12px 16px', background: publishedCount > 0 ? '#F0FDF4' : '#FFFBEB',
              borderRadius: 8, border: `1px solid ${publishedCount > 0 ? '#BBF7D0' : '#FDE68A'}`,
            }}>
              <p style={{ fontSize: 13, color: publishedCount > 0 ? '#15803D' : '#92400E', margin: 0, fontFamily: 'Montserrat, sans-serif' }}>
                {publishedCount > 0
                  ? `You have ${publishedCount} published event${publishedCount !== 1 ? 's' : ''} that will appear.`
                  : 'You have no published events yet. Create and publish events to show them here.'}
              </p>
            </div>
          )}

          {/* Manage events link */}
          <div style={{
            padding: '12px 16px', background: '#F9FAFB', borderRadius: 8,
            border: '1px solid #E5E7EB',
          }}>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 8px', fontFamily: 'Montserrat, sans-serif' }}>
              Events are managed from your dashboard — Services &amp; Events → Events tab.
            </p>
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate('/dashboard/services', { state: { tab: 'events' } })}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/services', { state: { tab: 'events' } })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 600, color: '#7c3aed',
                cursor: 'pointer', userSelect: 'none',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              Manage events
              <ExternalLink size={13} />
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default EventsPanel;
